/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { SummaryChip } from "components/scrapEyeDataDashboard/trainableAndCoverage/SummaryChip";
import { sorted } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { MouseEvent, useMemo } from "react";
import { useGetReviewerTrackingDailyQuery } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { DailyConsumptionImpactChart } from "./DailyConsumptionImpactChart";
import { DailyReviewerActionsChart } from "./DailyReviewerActionsChart";
import { aggregateEntries } from "./tenantReviewerAggregates";
import {
  Location,
  TOTAL_SELECTION,
  useLocationSelections,
} from "./useLocationSelections";

type TenantReviewerCardProps = Readonly<{
  tenantName: string;
  tenantLongName: string;
  start: string | undefined;
  end: string | undefined;
}>;

export const TenantReviewerCard = ({
  tenantName,
  tenantLongName,
  start,
  end,
}: TenantReviewerCardProps) => {
  const { t } = useTenantTranslation();
  const formatNumber0dp = useFormatNumber()(0);

  const { data, isLoading } = useGetReviewerTrackingDailyQuery(
    { tenantName, start: start ?? "", end: end ?? "" },
    { skip: !start || !end }
  );

  const entries = useMemo(() => data?.entries ?? [], [data?.entries]);

  const locations = useMemo<readonly Location[]>(() => {
    const uniqueNames = new Set(entries.map((entry) => entry.location_name));
    return sorted(
      [...uniqueNames].map((name) => ({ name })),
      (location) => location.name
    );
  }, [entries]);

  const hasMultipleLocations = locations.length > 1;

  const { selections, setSelections, selectedLocations, showTotal } =
    useLocationSelections(locations);

  const handleSelectionChange = (
    _event: MouseEvent<HTMLElement>,
    next: string[]
  ) => {
    if (next.length === 0) {
      return;
    }
    setSelections(next);
  };

  const summaryEntries = useMemo(() => {
    if (!hasMultipleLocations || showTotal) {
      return entries;
    }
    const selectedNames = new Set(
      selectedLocations.map((location) => location.name)
    );
    return entries.filter((entry) => selectedNames.has(entry.location_name));
  }, [entries, hasMultipleLocations, showTotal, selectedLocations]);

  const aggregateSummary = useMemo(
    () => aggregateEntries(summaryEntries),
    [summaryEntries]
  );

  const consumptionDrift =
    aggregateSummary.consumptionFalsePositives -
    aggregateSummary.consumptionFalseNegatives;

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6">{tenantLongName}</Typography>
          <CircularProgress size={32} />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            {tenantLongName}
          </Typography>
          {hasMultipleLocations && (
            <ToggleButtonGroup
              size="small"
              value={[...selections]}
              onChange={handleSelectionChange}
              sx={{
                "& .MuiToggleButton-root": {
                  px: 1.5,
                  py: 0.5,
                  textTransform: "none",
                },
              }}
            >
              {locations.map((location) => (
                <ToggleButton key={location.name} value={location.name}>
                  {location.name}
                </ToggleButton>
              ))}
              <ToggleButton value={TOTAL_SELECTION}>{t("total")}</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>

        <Stack spacing={0.5}>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <SummaryChip
              label={t("totalActions")}
              value={formatNumber0dp(aggregateSummary.totalActions)}
            />
            <SummaryChip
              label={t("consumptionFalseNegatives")}
              value={formatNumber0dp(
                aggregateSummary.consumptionFalseNegatives
              )}
            />
            <SummaryChip
              label={t("consumptionFalsePositives")}
              value={formatNumber0dp(
                aggregateSummary.consumptionFalsePositives
              )}
            />
            <SummaryChip
              label={t("consumptionDrift")}
              value={formatNumber0dp(consumptionDrift)}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t("selectedPeriod")}
          </Typography>
        </Stack>

        <Box
          sx={{
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 2,
            border: 1,
            borderColor: "grey.300",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
            {t("actionTracking")}
          </Typography>
          <DailyReviewerActionsChart
            entries={entries}
            selectedLocations={selectedLocations}
            showTotal={showTotal}
            isLoading={isLoading || !start || !end}
          />
        </Box>

        <Box
          sx={{
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 2,
            border: 1,
            borderColor: "grey.300",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
            {t("consumptionImpact")}
          </Typography>
          <DailyConsumptionImpactChart
            entries={entries}
            selectedLocations={selectedLocations}
            showTotal={showTotal}
            isLoading={isLoading || !start || !end}
          />
        </Box>
      </Stack>
    </Paper>
  );
};
